import React, { useCallback, useState, useEffect } from "react";
import { Form, Modal } from "@components";
import { Box, Button } from "@material-ui/core";
import { Manager } from "@components/form/state/types";
import { GsapFilters } from "../types";
import api from "@api";
import { GsapModel } from "@api/types/gsap";
import { useGsapStore } from "../../../../../../store/hooksy";
import { alertService } from "@services";

interface Props {
  open: boolean;
  onCancel: () => void;
  form: Manager<GsapFilters>;
}
type Key = keyof GsapFilters;

export const GsapDetail: React.FC<Props> = ({ open, onCancel, form }) => {
  const [, setGsapHooksyStore] = useGsapStore();

  let gsapModel: GsapModel = {
    id: 0,
    securityRole: "",
    accessType: "",
    isActive: false,
  };

  function clearGsapModel() {
    gsapModel.id = 0;
    gsapModel.securityRole = "";
    gsapModel.accessType = "";
    gsapModel.isActive = false;
    form.reset();
    onCancel();
  }

  function validate([name, value]: [string, any]): Promise<[Key, Error?]> {
    switch (name) {
      case "securityRole":
        return Promise.resolve([name as Key, Form.Validators.hasValue(value)]);
      case "accessType":
        return Promise.resolve([name as Key, Form.Validators.hasValue(value)]);
      default:
        return Promise.resolve([name as Key]);
    }
  }

  const [isSubmitting, setSubmitting] = useState(false);

  const handleSubmit = useCallback(() => {
    Promise.all(Object.entries(form.values).map(validate)).then(
      (validations) => {
        const valid = validations.reduce((valid, [name, error]) => {
          return valid && !error;
        }, true);
        if (valid) {
          submitData();
        } else {
          validations.forEach(([name, error]) => {
            form.set(Form.target.ERROR, [name, error]);
          });
        }
      }
    );
  }, [form]);

  function submitData() {
    gsapModel.id = form.values.id;
    gsapModel.securityRole = form.values.securityRole;
    gsapModel.accessType = form.values.accessType;
    gsapModel.isActive = form.values.isActive;
    setSubmitting(true);
    if (gsapModel.id !== 0) {
      // EDIT
      api.gsap
        .putGsap(gsapModel.id, gsapModel)
        .then((response) => {
          alertService.success({
            msg: "GSAP data has been successfully updated.",
          });
          clearGsapModel();
          setGsapHooksyStore({ fetchList: true });
        })
        .catch((error) => {
          if (error.response) {
            alertService.danger({
              msg: "default",
              statusCode: error.response.status,
            });
          }
        })
        .finally(() => {
          setSubmitting(false);
        });
    } else {
      // CREATE
      api.gsap
        .postGsap(gsapModel)
        .then((response) => {
          alertService.success({
            msg: "GSAP data has been successfully saved.",
          });
          clearGsapModel();
          setGsapHooksyStore({ fetchList: true });
        })
        .catch((error) => {
          if (error.response) {
            alertService.danger({
              msg: "default",
              statusCode: error.response.status,
            });
          }
        })
        .finally(() => {
          setSubmitting(false);
        });
    }
  }

  function onChange(name: Key, value: any): void {
    form.set(Form.target.VALUE, [name, value]);
    validate([name, value]).then(([name, error]) => {
      form.set(Form.target.ERROR, [name, error]);
    });
  }

  const accessTypes = [
    {
      label: "No Selection",
      value: "",
    },
    {
      label: "CMF Access",
      value: "CMF Access",
    },
    {
      label: "G2 Access",
      value: "G2 Access",
    },
    {
      label: "TDS Access",
      value: "TDS Access",
    },
    {
      label: "USAT Access",
      value: "USAT Access",
    },
  ];
  const modalTitle = form.values.id !== 0 ? "Update" : "Add New";
  const submitText = isSubmitting ? "Submitting..." : "Submit";
  return (
    <Modal
      isOpen={open}
      title={`${modalTitle} GSAP`}
      size="md"
      closeModal={() => clearGsapModel()}
    >
      <Form>
        <Form.Textbox
          label="Security Role"
          name="securityRole"
          onChange={onChange}
          size={12}
          error={form.errors.securityRole}
          value={form.values.securityRole}
          placeholder="Enter security role"
          required
        />
        <Form.Dropdown
          label="Access Type"
          onChange={onChange}
          options={accessTypes}
          name="accessType"
          error={form.errors.accessType}
          value={form.values.accessType}
          size={12}
          required
        />
        <Form.Checkbox
          label="Is Active"
          name="isActive"
          onChange={onChange}
          size={6}
          value={form.values.isActive}
        />
      </Form>
      <Box textAlign="right" mt={4}>
        <Button
          variant="outlined"
          color="primary"
          onClick={() => clearGsapModel()}
        >
          Cancel
        </Button>
        <Button
          variant="contained"
          color="primary"
          type="button"
          onClick={handleSubmit}
          style={{ marginLeft: "40px" }}
          disabled={isSubmitting}
        >
          {submitText}
        </Button>
      </Box>
    </Modal>
  );
};
